import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { z } from "zod";

export const reviewSchema = z.object({
  rating: z.number().min(1).max(5),
  title: z.string().min(3, "Title must be at least 3 characters").max(100, "Title must be less than 100 characters"),
  content: z.string().min(10, "Review must be at least 10 characters").max(1000, "Review must be less than 1000 characters"),
  reviewer_name: z.string().optional(),
  reviewer_email: z.string().email().optional(),
});

export type ReviewFormData = z.infer<typeof reviewSchema>;

interface SubmitReviewParams {
  productId: string;
  data: ReviewFormData;
  userId?: string;
}

export const useSubmitReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ productId, data, userId }: SubmitReviewParams) => {
      if (!userId) {
        throw new Error('You must be logged in to submit a review');
      }

      // Insert review - trigger will validate delivery and auto-populate fields
      const { data: review, error } = await supabase
        .from('product_reviews')
        .insert({
          product_id: productId,
          rating: data.rating,
          title: data.title,
          content: data.content,
        })
        .select()
        .single();

      if (error) {
        // Handle specific error for duplicate review
        if (error.code === '23505') {
          throw new Error('You have already reviewed this product');
        }
        throw error;
      }
      return review;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['enhanced-product'] });
      toast({
        title: "Review Submitted! 🎉",
        description: "Your review is pending approval and will be visible soon.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Submit Review",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });
};
